class CartProgressSteps extends HTMLElement {
  connectedCallback() {
    this.totalPrice = parseInt(this.dataset.totalPrice || '0', 10);
    this.market = (this.dataset.market || '').toUpperCase();
    this.markers = Array.from(this.querySelectorAll('.cart-steps__marker'));
    this.fill = this.querySelector('.cart-steps__fill');

    if (!this.markers.length || !this.fill) return;

    // data-amount supports market-specific thresholds: "FR:70,ES:80,DE:90"
    // Falls back to first value if the current market code is not found.
    this.amountsInCents = this.markers.map((m) => {
      const raw = m.dataset.amount || '';
      const resolved = this.resolveAmount(raw);
      // Update the displayed amount to match the resolved market value
      const amountEl = m.querySelector('.cart-steps__amount');
      if (amountEl) amountEl.textContent = resolved + '\u00a0\u20ac';
      return resolved * 100;
    });

    this.updateProgress();
  }

  /**
   * Parse "FR:70,ES:80,DE:90" and return the integer for the current market.
   * Accepts a plain integer string ("70") for backwards compatibility.
   * Falls back to the first entry if no match is found.
   */
  resolveAmount(raw) {
    const entries = raw.split(',').map((s) => s.trim()).filter(Boolean);
    if (!entries.length) return 0;

    // Plain number (no market prefix) → use as-is for all markets
    if (!entries[0].includes(':')) return parseInt(entries[0], 10) || 0;

    // Build a map of { CODE: value }
    const map = {};
    entries.forEach((entry) => {
      const [code, val] = entry.split(':');
      if (code && val) map[code.trim().toUpperCase()] = parseInt(val.trim(), 10) || 0;
    });

    // Exact match for current market
    if (this.market && map[this.market] !== undefined) return map[this.market];

    // Fallback: DEFAULT key, then first value
    if (map['DEFAULT'] !== undefined) return map['DEFAULT'];
    return Object.values(map)[0] || 0;
  }

  updateProgress() {
    const n = this.markers.length;

    // Activate markers and find the last reached step index
    let lastActive = -1;
    this.markers.forEach((marker, i) => {
      const reached = this.totalPrice >= this.amountsInCents[i];
      marker.classList.toggle('is-active', reached);
      if (reached) lastActive = i;
    });

    // With flex: 1 layout, the visual centre of marker i is at (i + 0.5) / n * 100%
    // The fill interpolates between those visual positions, not between real amounts.
    let fillPct;

    if (lastActive === n - 1) {
      // All steps reached → full bar
      fillPct = 100;
    } else if (lastActive === -1) {
      // No step reached yet → interpolate toward the first marker's centre
      const firstCentre = (0.5 / n) * 100;
      const progress = this.amountsInCents[0] > 0
        ? this.totalPrice / this.amountsInCents[0]
        : 0;
      fillPct = progress * firstCentre;
    } else {
      // Some steps reached → interpolate between lastActive centre and next centre
      const currentCentre = (lastActive + 0.5) / n * 100;
      const nextCentre = (lastActive + 1.5) / n * 100;
      const span = this.amountsInCents[lastActive + 1] - this.amountsInCents[lastActive];
      const progress = span > 0
        ? (this.totalPrice - this.amountsInCents[lastActive]) / span
        : 1;
      fillPct = currentCentre + progress * (nextCentre - currentCentre);
    }

    this.fill.style.width = `${fillPct.toFixed(2)}%`;
  }
}

customElements.define('cart-progress-steps', CartProgressSteps);
